import { useState, useEffect } from "react";
import { Deck } from "@/lib/classes/deck";
import { UnitCategory } from "@/lib/types/deck-builder";
import type { Pack, DeckUnit } from "@/lib/types/deck-builder";
import type { Unit } from "@/lib/types/unit";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Info, ChevronUp, Minus } from "lucide-react";
import { UnitSelectionDialog } from "./UnitSelectionDialog";
import { UnitInfoDialog } from "./UnitInfoDialog";
import { useComparisonModals } from "./ComparisonModalManager";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { getTraitIconPath, filterTraits } from "@/lib/utils/trait-icons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SlotCostGrid } from "@/components/SlotCostGrid";

// Helper function to get category code from category enum
function getCategoryCode(category: UnitCategory): string {
  const codeMap: Record<UnitCategory, string> = {
    [UnitCategory.LOG]: "LOG",
    [UnitCategory.INF]: "INF",
    [UnitCategory.ART]: "ART",
    [UnitCategory.TNK]: "TNK",
    [UnitCategory.REC]: "REC",
    [UnitCategory.AA]: "AA",
    [UnitCategory.HEL]: "HEL",
    [UnitCategory.AIR]: "AIR",
    [UnitCategory.DEFENSE]: "DEF",
  };
  return codeMap[category] || category.split("/").pop() || "";
}

interface ArmouryViewProps {
  deck: Deck;
  selectedCategory?: UnitCategory | null;
  onAddUnit?: (pack: Pack, veterancy: number, transport?: Unit) => void;
}

export function ArmouryView({ deck, selectedCategory, onAddUnit }: ArmouryViewProps) {
  const [selectedPack, setSelectedPack] = useState<Pack | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const availableUnits = deck.availableUnits;
  const categoriesToShow = selectedCategory
    ? [selectedCategory]
    : deck.unitCategories;

  const handleAddClick = (pack: Pack, unit: Unit) => {
    setSelectedPack(pack);
    setSelectedUnit(unit);
    setDialogOpen(true);
  };

  const handleConfirm = (pack: Pack, veterancy: number, transport?: Unit) => {
    if (onAddUnit) {
      onAddUnit(pack, veterancy, transport);
    }
    setDialogOpen(false);
    setSelectedPack(null);
    setSelectedUnit(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedPack(null);
    setSelectedUnit(null);
  };

  return (
    <div className="min-w-0 w-full">
      <Accordion type="multiple" defaultValue={categoriesToShow} className="w-full min-w-0">
        {categoriesToShow.map((category) => {
          const categoryUnits = availableUnits[category];
          if (!categoryUnits || Object.keys(categoryUnits).length === 0) {
            return null;
          }

          // Group packs by speciality for display
          const packsBySpeciality = Object.entries(categoryUnits);

          const slotCosts = deck.slotCosts[category] || [];
          const nextSlotIndex = deck.getNextSlotCostIndexForCategory(category) ?? 0;

          return (
            <AccordionItem key={category} value={category} className="border-b last:border-b-0">
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex min-w-0 flex-1 flex-row items-center gap-1 pr-2 sm:pr-4">
                  <h4 className="shrink-0 pr-2 text-sm font-semibold uppercase tracking-wide text-foreground">
                    {getCategoryCode(category)}
                  </h4>
                  <div className="min-w-0 flex-1">
                    <SlotCostGrid costs={slotCosts} filledSlotCount={nextSlotIndex} />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-1 pt-2">
                  {packsBySpeciality.map(([speciality, packs]) => {
                    if (packs.length === 0) return null;

                    return (
                      <div key={speciality} className="space-y-1 sm:space-y-2">
                        <div className="grid gap-1.5 sm:gap-2 justify-items-center" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 160px))' }}>
                          {packs.map((pack) => {
                            const unit = deck.getUnitForPack(pack);
                            if (!unit) return null;

                            const available = deck.getAvailableQuantityOfPack(pack);
                            const canAdd = available > 0;

                                return (
                                  <UnitCard
                                    key={pack.packDescriptor}
                                    unit={unit}
                                    pack={pack}
                                    available={available}
                                    canAdd={canAdd}
                                    onAddClick={() => handleAddClick(pack, unit)}
                                    deck={deck}
                                    onAddUnit={onAddUnit}
                                  />
                                );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
      
      <UnitSelectionDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        pack={selectedPack}
        unit={selectedUnit}
        deck={deck}
        onConfirm={handleConfirm}
      />
    </div>
  );
}

interface UnitCardProps {
  unit: Unit;
  pack: Pack;
  available: number;
  canAdd: boolean;
  onAddClick: () => void;
  deck: Deck;
  onAddUnit?: (pack: Pack, veterancy: number, transport?: Unit) => void;
}

function UnitCard({ unit, pack, available, canAdd, onAddClick, deck, onAddUnit }: UnitCardProps) {
  const [imageError, setImageError] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [traitImageErrors, setTraitImageErrors] = useState<Set<number>>(new Set());
  const [selectedVeterancy, setSelectedVeterancy] = useState<number>(0);
  const [transportDialogOpen, setTransportDialogOpen] = useState(false);
  const { openComparisonModal } = useComparisonModals();

  // Get veterancy quantities for this pack
  const veterancyQuantities = Deck.getVeterancyQuantitiesForPack(pack, unit);
  const defaultVeterancy = Deck.getDefaultVeterancyForPack(pack, unit);

  // Initialize selected veterancy when pack/unit change (e.g. supply: vet > 0 disabled)
  useEffect(() => {
    const quantities = Deck.getVeterancyQuantitiesForPack(pack, unit);
    const def = Deck.getDefaultVeterancyForPack(pack, unit);
    if (def >= 0 && quantities[def] !== undefined && quantities[def] > 0) {
      setSelectedVeterancy(def);
    } else {
      const firstAvailable = quantities.findIndex((qty) => qty > 0);
      if (firstAvailable >= 0) {
        setSelectedVeterancy(firstAvailable);
      }
    }
  }, [pack.packDescriptor, unit.descriptorName]);

  // Get available cards count
  const availableCards = deck.getAvailableQuantityOfPack(pack);
  
  // Get units per card for selected veterancy
  const unitsPerCard = veterancyQuantities[selectedVeterancy] || 0;
  
  // Format unit name with command prefix if applicable
  const unitNameDisplay = unit.name;
  
  // Filter and get first 3 traits (specialities starting with "_")
  const allTraits = filterTraits(unit.specialities || []);
  const traitIcons = allTraits;

  const handleVeterancyClick = (veterancy: number) => {
    const quantity = veterancyQuantities[veterancy];
    if (quantity !== undefined && quantity > 0) {
      setSelectedVeterancy(veterancy);
    }
  };

  const handleAddClickWithVeterancy = () => {
    const transports = deck.getTransportsForPack(pack);
    const canUseWithoutTransport = deck.getPackAvailableWithoutTransport(pack);
    const requiresTransport = transports && transports.length > 0 && !canUseWithoutTransport;

    if (requiresTransport) {
      // If there's exactly one transport option, auto-select it
      if (transports.length === 1) {
        if (onAddUnit) {
          onAddUnit(pack, selectedVeterancy, transports[0]);
        }
      } else {
        // Multiple transport options - show selection dialog
        setTransportDialogOpen(true);
      }
    } else {
      // Can add directly without transport
      if (onAddUnit) {
        onAddUnit(pack, selectedVeterancy);
      }
    }
  };

  return (
    <>
      <Card className={`overflow-hidden max-w-[160px] w-full transition-opacity ${!canAdd ? 'opacity-50' : ''}`}>
        <CardContent className="p-0">
          <div className="flex flex-col">
            {/* Trait Icons Header */}
            <div className="flex items-center justify-center gap-0.5 sm:gap-1 px-1 sm:px-2 h-[20px] sm:h-[24px] lg:h-[28px]">
              {traitIcons.length > 0 && (
                <>
                  {traitIcons.map((trait, index) => {
                  const iconPath = getTraitIconPath(trait);
                  const hasError = traitImageErrors.has(index);
                  
                  return (
                    <div
                      key={index}
                      className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5 rounded bg-pink-500/20 border border-pink-500/40 flex items-center justify-center overflow-hidden"
                      title={trait}
                    >
                      {iconPath && !hasError ? (
                        <img
                          src={iconPath}
                          alt={trait}
                          className="w-full h-full object-contain"
                          style={{
                            filter: 'var(--trait-icon-filter)'
                          }}
                          onError={() => {
                            setTraitImageErrors(prev => new Set(prev).add(index));
                          }}
                        />
                      ) : (
                        <span className="text-[8px] sm:text-[9px] lg:text-[10px] text-pink-500 font-bold">
                          {trait.replace('_', '').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                  );
                  })}
                </>
              )}
            </div>

            {/* Main Image Area with Overlays */}
            <div className="relative w-full min-h-[50px] sm:min-h-[60px] lg:min-h-[76px] bg-muted flex items-center justify-center">
              {!imageError ? (
                <img
                  src={getUnitImagePath(unit.descriptorName)}
                  alt={unit.name}
                  className="w-full h-auto"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-full min-h-[50px] sm:min-h-[60px] lg:min-h-[76px] flex items-center justify-center bg-muted">
                  <span className="text-[10px] sm:text-xs text-muted-foreground">No Image</span>
                </div>
              )}

              {/* Overlay Add Button (top-left) */}
              {canAdd && (
                <button
                  onClick={handleAddClickWithVeterancy}
                  className="absolute top-[1px] sm:top-[2px] left-[1px] sm:left-[2px] w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 bg-pink-500 hover:bg-pink-600 rounded flex items-center justify-center shadow-lg transition-colors z-10"
                  aria-label="Add unit"
                >
                  <Plus className="h-3 w-3 sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4 text-white" />
                </button>
              )}

              {/* Overlay Info Button (top-right) */}
              <button
                onClick={() => {
                  // On desktop, open comparison modal directly; on mobile, use regular modal
                  if (typeof window !== "undefined" && window.innerWidth >= 1024) {
                    openComparisonModal(unit);
                  } else {
                    setInfoDialogOpen(true);
                  }
                }}
                className="absolute top-[1px] sm:top-[2px] right-[1px] sm:right-[2px] w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center shadow-lg transition-colors z-10"
                aria-label="Unit information"
              >
                <Info className="h-2.5 w-2.5 sm:h-3 sm:w-3 lg:h-3.5 lg:w-3.5 text-white" />
              </button>

              {/* Quantity Indicator (bottom-left) - units per card for selected veterancy */}
              <div className="absolute bottom-[1px] sm:bottom-[2px] left-[1px] sm:left-[2px] bg-black/70 px-1 sm:px-1.5 py-0.5 rounded text-white text-[10px] sm:text-xs font-semibold">
                x{unitsPerCard}
              </div>

              {/* Cost Indicator (bottom-right) */}
              <div className="absolute bottom-[1px] sm:bottom-[2px] right-[1px] sm:right-[2px] bg-black/70 px-1 sm:px-1.5 py-0.5 rounded text-white text-[10px] sm:text-xs font-semibold">
                {unit.commandPoints}
              </div>
            </div>

            {/* Unit Identifier Text */}
            <div className="px-1 sm:px-1.5 lg:px-2 py-1 sm:py-1.5 bg-card border-t">
              <div className="text-[10px] sm:text-xs font-medium text-center leading-tight flex items-center justify-center gap-1.5 min-w-0">
                <Badge variant="secondary" className="text-[6px] sm:text-xs flex-shrink-0">
                  {availableCards}
                </Badge>
                <span className="truncate">{unitNameDisplay}</span>
              </div>
            </div>

            {/* Veterancy Selection Buttons */}
            <div className="px-1 sm:px-1.5 lg:px-2 pb-0.5 sm:pb-1 pt-1 sm:pt-1.5 flex items-center justify-center gap-0.5 sm:gap-1">
              {veterancyQuantities.map((quantity, index) => {
                const isSelected = selectedVeterancy === index;
                const isDisabled = quantity === 0;
                
                // Determine icon based on veterancy level
                let icon;
                if (index === 0) {
                  icon = <Minus className="h-3 w-3 sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4 stroke-[2.5]" />;
                } else if (index === 1) {
                  icon = <ChevronUp className="h-3 w-3 sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4 stroke-[2.5]" />;
                } else if (index === 2) {
                  icon = (
                    <div className="flex flex-col items-center justify-center -space-y-0.5" style={{ width: '12px', minWidth: '12px' }}>
                      <ChevronUp className="h-2.5 sm:h-2.5 lg:h-3 stroke-[2.5]" style={{ width: '12px', minWidth: '12px' }} />
                      <ChevronUp className="h-2.5 sm:h-2.5 lg:h-3 stroke-[2.5]" style={{ width: '12px', minWidth: '12px' }} />
                    </div>
                  );
                } else if (index === 3) {
                  icon = (
                    <div className="flex flex-col items-center justify-center -space-y-0.5" style={{ width: '12px', minWidth: '12px' }}>
                      <ChevronUp className="h-2 sm:h-2.5 lg:h-2.5 stroke-[2.5]" style={{ width: '12px', minWidth: '12px' }} />
                      <ChevronUp className="h-2 sm:h-2.5 lg:h-2.5 stroke-[2.5]" style={{ width: '12px', minWidth: '12px' }} />
                      <ChevronUp className="h-2 sm:h-2.5 lg:h-2.5 stroke-[2.5]" style={{ width: '12px', minWidth: '12px' }} />
                    </div>
                  );
                } else {
                  icon = <span className="text-[8px] sm:text-[9px] lg:text-[10px] font-bold">{index}</span>;
                }

                return (
                  <button
                    key={index}
                    onClick={() => handleVeterancyClick(index)}
                    disabled={isDisabled}
                    className={`
                      relative flex-1 max-w-[36px] sm:max-w-[40px] lg:max-w-[48px] h-6 sm:h-7 lg:h-8 flex items-center justify-center
                      border-2 rounded-md transition-colors
                      ${isSelected 
                        ? 'border-pink-500 bg-pink-500/20' 
                        : 'border-pink-500/40 bg-pink-500/10 hover:bg-pink-500/20'
                      }
                      ${isDisabled 
                        ? 'opacity-30 cursor-not-allowed' 
                        : 'cursor-pointer'
                      }
                    `}
                    title={`Veterancy ${index}: ${quantity} units per card`}
                  >
                    <div className="text-foreground w-3 sm:w-3.5 lg:w-4 flex items-center justify-center">
                      {icon}
                    </div>
                    {quantity > 0 && (
                      <span className="absolute -bottom-0.5 sm:-bottom-1 -right-0.5 sm:-right-1 text-[7px] sm:text-[8px] text-pink-500 font-bold bg-card rounded-full w-3 h-3 sm:w-3.5 sm:h-3.5 flex items-center justify-center border border-pink-500/40">
                        {quantity}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <UnitInfoDialog
        open={infoDialogOpen}
        onOpenChange={setInfoDialogOpen}
        unit={unit}
      />
      
      {transportDialogOpen && (
        <UnitSelectionDialog
          open={transportDialogOpen}
          onClose={() => setTransportDialogOpen(false)}
          pack={pack}
          unit={unit}
          deck={deck}
          preselectedVeterancy={selectedVeterancy}
          hideVeterancySelection={true}
          onConfirm={(pack, veterancy, transport) => {
            if (onAddUnit) {
              onAddUnit(pack, selectedVeterancy, transport);
            }
            setTransportDialogOpen(false);
          }}
        />
      )}
    </>
  );
}

